// Message source constants
export const BINTACT_SOURCE = "BINTACT";
export const BINTACT_AGRO_INFORMATICS_SOURCE = "BINTACT:AGRO-INFORMATICS";

// Message type constants
export const MESSAGE_TYPES = {
  READY: "ready",
  OPEN_MAP: "OPEN_MAP",
  SAVE_AND_EXIT_REQUEST: "SAVE_AND_EXIT_REQUEST",
  SAVE_AND_EXIT_RESPONSE: "SAVE_AND_EXIT_RESPONSE",
  EDIT_STARTED: "EDIT_STARTED",
  SET_BASE_LINE: "SET_BASE_LINE",
  SET_BASE_LINE_RESPONSE: "SET_BASE_LINE_RESPONSE",
} as const;

// Incoming message types (from map service)
export type ReadyMessage = typeof MESSAGE_TYPES.READY;

export interface SaveAndExitResponse {
  source: typeof BINTACT_AGRO_INFORMATICS_SOURCE;
  message: typeof MESSAGE_TYPES.SAVE_AND_EXIT_RESPONSE;
  failed: boolean;
  errorMessage?: string;
  isPainting?: true;
}

export interface EditingStartedMessage {
  source: typeof BINTACT_AGRO_INFORMATICS_SOURCE;
  message: typeof MESSAGE_TYPES.EDIT_STARTED;
  projectId: string;
}

export interface SetBaseLineResponse {
  source: typeof BINTACT_AGRO_INFORMATICS_SOURCE;
  message: typeof MESSAGE_TYPES.SET_BASE_LINE_RESPONSE;
  failed: boolean;
  errorMessage?: string;
}

// Outgoing message types (to map service)
export interface OpenMapPayload {
  source: typeof BINTACT_SOURCE;
  message: typeof MESSAGE_TYPES.OPEN_MAP;
  projectId: string;
  projectName: string;
  activityAreaId?: string;
  activityAreaName?: string;
  loadBaseLine?: boolean;
}

export interface SaveAndExitRequest {
  source: typeof BINTACT_SOURCE;
  message: typeof MESSAGE_TYPES.SAVE_AND_EXIT_REQUEST;
}

export interface SetBaseLineRequest {
  source: typeof BINTACT_SOURCE;
  message: typeof MESSAGE_TYPES.SET_BASE_LINE;
}

// Union type for all incoming messages
export type BintactIncomingMessage = ReadyMessage | SaveAndExitResponse | EditingStartedMessage | SetBaseLineResponse;

// Type guard functions
export function isReadyMessage(message: unknown): message is ReadyMessage {
  if (typeof message === "string") {
    return message.includes(MESSAGE_TYPES.READY);
  }
  return false;
}

export function isSaveAndExitResponse(message: unknown): message is SaveAndExitResponse {
  if (typeof message !== "object" || message === null) return false;
  
  const msg = message as Record<string, unknown>;
  return (
    msg.message === MESSAGE_TYPES.SAVE_AND_EXIT_RESPONSE &&
    typeof msg.source === "string" &&
    typeof msg.failed === "boolean"
  );
}

export function isEditingStartedMessage(message: unknown): message is EditingStartedMessage {
  if (typeof message !== "object" || message === null) return false;
  
  const msg = message as Record<string, unknown>;
  return (
    msg.message === MESSAGE_TYPES.EDIT_STARTED &&
    typeof msg.source === "string" 
    //&& typeof msg.projectId === "string" // TODO: add projectId validation when they add projectId
  );
}

export function isSetBaseLineResponse(message: unknown): message is SetBaseLineResponse {
  if (typeof message !== "object" || message === null) return false;
  
  const msg = message as Record<string, unknown>;
  return (
    msg.message === MESSAGE_TYPES.SET_BASE_LINE_RESPONSE &&
    typeof msg.source === "string" &&
    typeof msg.failed === "boolean"
  );
}

// Message builders/factories
export function createOpenMapPayload(
  projectId: string,
  projectName: string,
  activityAreaId?: string,
  activityAreaName?: string,
  loadBaseLine?: boolean
): OpenMapPayload {
  const payload: OpenMapPayload = {
    source: BINTACT_SOURCE,
    message: MESSAGE_TYPES.OPEN_MAP,
    projectId,
    projectName,
  };
  
  if (activityAreaId) {
    payload.activityAreaId = activityAreaId;
  }
  
  if (activityAreaName) {
    payload.activityAreaName = activityAreaName;
  }
  
  if (loadBaseLine) {
    payload.loadBaseLine = true;
  }
  
  return payload;
}

export function createSaveAndExitRequest(): SaveAndExitRequest {
  return {
    source: BINTACT_SOURCE,
    message: MESSAGE_TYPES.SAVE_AND_EXIT_REQUEST,
  };
}

export function createSetBaseLineRequest(): SetBaseLineRequest {
  return {
    source: BINTACT_SOURCE,
    message: MESSAGE_TYPES.SET_BASE_LINE,
  };
}
